<!--
  通用底部弹窗外壳：遮罩 + 从底部升起的面板 + 右上角关闭
  各任务的规则弹窗都复用这一层，内容通过 children 传入
-->
<script lang="ts">
  import type { Snippet } from "svelte";
  import { onDestroy } from "svelte";
  import { portal } from "svelte-portal";
  import { isEmbedded } from "../../libs/chief-bridge";
  import { lockScroll, unlockScroll } from "../lib/scroll-lock";

  interface Props {
    show: boolean;
    onClose: () => void;
    /** 是否显示右上角关闭按钮 */
    showClose?: boolean;
    /** 是否带默认内边距（抽奖码弹窗的头图要通栏，需要传 false） */
    padded?: boolean;
    children?: Snippet;
  }

  let {
    show,
    onClose,
    showClose = true,
    padded = true,
    children,
  }: Props = $props();

  // 非 WebView 环境限宽 640，跟页面容器保持一致
  const widthStyle = isEmbedded() ? "" : "max-width: 640px;";

  // 跟踪当前实例是否持有锁，用于 onDestroy 兜底
  let hasLock = false;

  // 打开时锁住背景滚动
  // 使用全局引用计数器（scroll-lock.ts）处理多个 BottomSheet/Dialog 同时存在的情况：
  // 关闭第一个时不会提前解除其他弹窗的锁。
  // iOS Safari 需要同时锁 html 和 body，否则橡皮筋弹性滚动仍会穿透。
  $effect(() => {
    if (!show) return;
    lockScroll();
    hasLock = true;
    return () => {
      unlockScroll();
      hasLock = false;
    };
  });

  // 兜底：组件在 show=true 时被意外销毁（父级 {#if} 切走等场景），
  // $effect 清理函数来不及执行，onDestroy 确保滚动锁一定被释放。
  // 只有当前实例确实持有锁时才释放，避免误伤其他弹窗。
  onDestroy(() => {
    if (hasLock) {
      unlockScroll();
      hasLock = false;
    }
  });
</script>

{#if show}
  <!--
    portal 到 body：页面滚动容器是 NavBar 的 .nav-content，
    fixed 遮罩留在滚动容器里关闭时会有残影闪一下
  -->
  <div
    class="sheet-mask"
    use:portal
    role="presentation"
    onclick={onClose}
  ></div>

  <div
    class="sheet"
    class:is-plain={!padded}
    use:portal
    style={widthStyle}
  >
    {#if showClose}
      <button
        type="button"
        class="sheet-close"
        aria-label="close"
        onclick={onClose}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M5 5L15 15M15 5L5 15"
            stroke="#FFFFFF"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </button>
    {/if}

    {#if children}
      {@render children()}
    {/if}
  </div>
{/if}

<style>
  .sheet-mask {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 200;
    background: rgba(0, 0, 0, 0.5);
  }

  /*
    设计稿：满页宽 375 / 上圆角 24 / 渐变 #68ADFF → #D6EEFE
    高度完全由内容决定（入金弹窗三行档位刚好 467，交易弹窗只有一行会更矮）。
    注意：不要在这里给整个 .sheet 加 overflow-y/max-height——头部装饰图
    （如抽奖码弹窗的 lc-header-deco）是负 top 溢出到 sheet 顶边外的，
    整体裁切会把装饰图切掉。内容可能超长的弹窗（抽奖码列表）自己在内部
    做局部滚动，不影响这里。
  */
  .sheet {
    position: fixed;
    bottom: 0;
    left: 50%;
    z-index: 201;
    width: 100%;
    transform: translateX(-50%);
    /* 底部安全距离已经算在设计稿的 467 高度里（站内 App 也已处理），这里不再叠加 env() */
    padding: 26px 16px 16px;
    border-radius: 24px 24px 0 0;
    background: linear-gradient(180deg, #68adff 0%, #d6eefe 100%);
  }

  /* 通栏模式：内边距交给内容自己控制 */
  .sheet.is-plain {
    padding: 0;
  }

  /*
    图标还是 20x20，但点击区域扩到 44x44（图标中心保持在距顶 / 距右各 26px）
    所以 top / right 用 26 - 22 = 4px
  */
  .sheet-close {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    line-height: 0;
  }
</style>
